import type {
  AIChatMessage,
  AskUserAnswers,
  AskUserFieldSchema,
  AskUserOption,
  AskUserQuestion,
  AskUserRequest,
  ChatActionResultPayload,
  LaneSearchCharge,
  LaneSearchLeg,
  LaneSearchQuote,
  LaneSearchResultData,
  LaneSearchTrucking,
  LaneSearchTruckingEnd,
  LaneSearchTruckingLeg,
} from './types';

/** What the panel asks in one step: a single question, or the questions an
 * action declared as one group (`x-ui.group`). */
export type AskUserStep = {
  /** The group's title; null for a question asked on its own. */
  title: string | null;
  questions: AskUserQuestion[];
};

/** Questions sharing an `x-ui.group` become one step, placed where the
 * group's first question was; every other question is a step of its own.
 * A group with a single question is just that question. */
export function askUserSteps(questions: AskUserQuestion[]): AskUserStep[] {
  const steps: AskUserStep[] = [];
  const groups = new Map<string, AskUserStep>();
  for (const question of questions) {
    const group = question.schema['x-ui']?.group;
    const existing = group ? groups.get(group) : undefined;
    if (existing) {
      existing.questions.push(question);
    } else {
      const step = { title: group ?? null, questions: [question] };
      if (group) groups.set(group, step);
      steps.push(step);
    }
  }
  return steps.map((step) =>
    step.questions.length === 1 ? { ...step, title: null } : step
  );
}

// Past this many choices a radio list stops being scannable in the drawer's
// narrow column, so a single-choice question renders as a dropdown instead.
export const MAX_RADIO_OPTIONS = 6;

export type AskUserFieldKind =
  | 'single_choice'
  | 'multi_choice'
  | 'boolean'
  | 'number'
  | 'text'
  | 'rows'
  | 'form';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isOptionList(value: unknown): value is AskUserOption[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((option) => isRecord(option) && 'const' in option)
  );
}

/** Which input a question's schema renders as. Anything that isn't one of
 * the known shapes is a plain text input -- the panel never refuses to ask
 * a question just because its schema is unfamiliar. */
export function askUserFieldKind(schema: AskUserFieldSchema): AskUserFieldKind {
  // Any type can carry titled options, e.g. a yes/no shown as
  // "Total" / "Per item".
  if (schema.type !== 'array' && isOptionList(schema.oneOf)) {
    return 'single_choice';
  }
  if (schema.type === 'array' && isOptionList(schema.items?.anyOf)) {
    return 'multi_choice';
  }
  if (
    schema.type === 'array' &&
    schema.items?.type === 'object' &&
    isRecord(schema.items.properties)
  ) {
    return 'rows';
  }
  // A group of plain fields (e.g. the trucking cargo): one screen of inputs.
  if (schema.type === 'object' && isRecord(schema.properties)) return 'form';
  if (schema.type === 'boolean') return 'boolean';
  if (schema.type === 'integer' || schema.type === 'number') return 'number';
  return 'text';
}

export function optionTitle(option: AskUserOption): string {
  return typeof option.title === 'string' && option.title
    ? option.title
    : String(option.const);
}

/** A rows field's columns, in the order the schema declares them. */
export function rowProperties(
  schema: AskUserFieldSchema
): [string, AskUserFieldSchema][] {
  return Object.entries(schema.items?.properties ?? {});
}

/** A group's fields, in the order the schema declares them. */
export function groupProperties(
  schema: AskUserFieldSchema
): [string, AskUserFieldSchema][] {
  return Object.entries(schema.properties ?? {});
}

/** A rows field's columns grouped into lines: consecutive columns with the
 * same `x-ui.row` sit side by side; every other column has a line to
 * itself. */
export function rowLayout(
  schema: AskUserFieldSchema
): [string, AskUserFieldSchema][][] {
  return layoutLines(rowProperties(schema));
}

/** A group's fields grouped into lines the same way: consecutive fields with
 * the same `x-ui.row` sit side by side. */
export function formLayout(
  schema: AskUserFieldSchema
): [string, AskUserFieldSchema][][] {
  return layoutLines(groupProperties(schema));
}

function layoutLines(
  columns: [string, AskUserFieldSchema][]
): [string, AskUserFieldSchema][][] {
  const lines: [string, AskUserFieldSchema][][] = [];
  let current: string | undefined;
  for (const column of columns) {
    const row = column[1]['x-ui']?.row;
    if (row && row === current) {
      lines[lines.length - 1].push(column);
    } else {
      lines.push([column]);
    }
    current = row;
  }
  return lines;
}

/** The unit every column in a line shares (shown once, in its own "Unit"
 * column), or undefined when they don't all share one. */
export function sharedUnit(
  line: [string, AskUserFieldSchema][]
): string | undefined {
  const units = new Set(line.map(([, property]) => property['x-ui']?.unit));
  const [unit] = units;
  return line.length > 1 && units.size === 1 ? unit : undefined;
}

/** The value a question starts at: its schema `default`, else the empty
 * value for its input (so every key still has a well-typed value). */
export function askUserInitialValue(schema: AskUserFieldSchema): unknown {
  const kind = askUserFieldKind(schema);
  if (schema.default !== undefined) {
    if (kind === 'form' && isRecord(schema.default)) {
      // What the last call already gave wins over each field's own start.
      return { ...emptyForm(schema), ...schema.default };
    }
    if (kind === 'rows' && Array.isArray(schema.default)) {
      // A default row may leave out columns with their own defaults (e.g.
      // stackable) -- fill those in so the inputs start where the action
      // would.
      return schema.default.map((row) => ({
        ...emptyRow(schema),
        ...(isRecord(row) ? row : {}),
      }));
    }
    return schema.default;
  }
  if (kind === 'multi_choice') return [];
  if (kind === 'boolean') return false;
  if (kind === 'rows') {
    return Array.from({ length: schema.minItems ?? 1 }, () => emptyRow(schema));
  }
  if (kind === 'text') return '';
  if (kind === 'form') return emptyForm(schema);
  return undefined;
}

/** A group's fields at their starting values. A choice with only one option
 * (the trucking cargo's type) starts on it, since there is nothing to pick. */
function emptyForm(schema: AskUserFieldSchema): Record<string, unknown> {
  const form: Record<string, unknown> = {};
  for (const [name, property] of groupProperties(schema)) {
    const options = property.oneOf;
    const initial =
      property.default === undefined &&
      askUserFieldKind(property) === 'single_choice' &&
      options?.length === 1
        ? options[0].const
        : askUserInitialValue(property);
    if (initial !== undefined) form[name] = initial;
  }
  return form;
}

export function emptyRow(schema: AskUserFieldSchema): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const [name, property] of rowProperties(schema)) {
    const initial = askUserInitialValue(property);
    if (initial !== undefined) row[name] = initial;
  }
  return row;
}

/** Nothing to report for this cell: blank, or a list with nothing ticked. */
function isEmptyCell(value: unknown): boolean {
  return isBlank(value) || (Array.isArray(value) && value.length === 0);
}

function withoutEmptyCells(
  record: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(record).filter(([, cell]) => !isEmptyCell(cell))
  );
}

/** What is sent for an answer: a group without its blank fields, so a field
 * left empty is left out of the request and its own default applies. */
export function askUserSubmitValue(
  schema: AskUserFieldSchema,
  value: unknown
): unknown {
  return askUserFieldKind(schema) === 'form' && isRecord(value)
    ? withoutEmptyCells(value)
    : value;
}

function isBlank(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '')
  );
}

/** Why `value` can't be submitted for this schema yet, or null when it can.
 * Single choice and number questions need an answer; everything else is
 * only held to the constraints its schema actually declares. */
export function askUserFieldError(
  schema: AskUserFieldSchema,
  value: unknown
): string | null {
  const kind = askUserFieldKind(schema);
  if (kind === 'single_choice') {
    return isBlank(value) ? 'Choose an option.' : null;
  }
  if (kind === 'number') {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return 'Enter a number.';
    }
    if (schema.type === 'integer' && !Number.isInteger(value)) {
      return 'Enter a whole number.';
    }
    if (schema.minimum !== undefined && value < schema.minimum) {
      return `Must be at least ${schema.minimum}.`;
    }
    if (schema.maximum !== undefined && value > schema.maximum) {
      return `Must be at most ${schema.maximum}.`;
    }
    return null;
  }
  if (kind === 'multi_choice' || kind === 'rows') {
    const items = Array.isArray(value) ? value : [];
    if (schema.minItems !== undefined && items.length < schema.minItems) {
      return `Add at least ${schema.minItems}.`;
    }
    if (schema.maxItems !== undefined && items.length > schema.maxItems) {
      return `Add at most ${schema.maxItems}.`;
    }
    if (kind === 'rows') {
      const required = new Set(schema.items?.required ?? []);
      for (const row of items) {
        for (const [name, property] of rowProperties(schema)) {
          const cell = isRecord(row) ? row[name] : undefined;
          if (isBlank(cell)) {
            if (required.has(name)) return 'Fill in every required field.';
            continue;
          }
          const error = askUserFieldError(property, cell);
          if (error) return error;
        }
      }
    }
    return null;
  }
  if (kind === 'form') {
    const record = isRecord(value) ? value : {};
    const required = new Set(schema.required ?? []);
    for (const [name, property] of groupProperties(schema)) {
      const cell = record[name];
      if (isBlank(cell)) {
        if (required.has(name)) return 'Fill in every required field.';
        continue;
      }
      const error = askUserFieldError(property, cell);
      if (error) return error;
    }
    return null;
  }
  if (kind === 'text') {
    const text = typeof value === 'string' ? value : '';
    if (schema.format === 'date' && !text) return 'Choose a date.';
    if (schema.minLength !== undefined && text.length < schema.minLength) {
      return `Enter at least ${schema.minLength} characters.`;
    }
    if (schema.maxLength !== undefined && text.length > schema.maxLength) {
      return `Enter at most ${schema.maxLength} characters.`;
    }
    if (text && schema.pattern) {
      try {
        if (!new RegExp(schema.pattern, 'u').test(text)) {
          return 'Check the format.';
        }
      } catch {
        // An invalid pattern is the payload's problem -- don't block on it.
      }
    }
  }
  return null;
}

/** Human-readable form of a raw answer, using the schema's display titles
 * (never the raw const codes) -- shared by the submitted message summary
 * and the read-only replay of a past question. */
export function formatAskUserValue(
  schema: AskUserFieldSchema,
  value: unknown
): string {
  const kind = askUserFieldKind(schema);
  if (kind === 'single_choice') {
    const option = schema.oneOf?.find((item) => item.const === value);
    return option ? optionTitle(option) : formatScalar(value);
  }
  if (kind === 'multi_choice') {
    const values = Array.isArray(value) ? value : [];
    if (values.length === 0) return 'None';
    return values
      .map((item) => {
        const option = schema.items?.anyOf?.find((o) => o.const === item);
        return option ? optionTitle(option) : formatScalar(item);
      })
      .join(', ');
  }
  if (kind === 'boolean') return value ? 'Yes' : 'No';
  if (kind === 'form') {
    const record = isRecord(value) ? value : {};
    return groupProperties(schema)
      .filter(([name]) => !isEmptyCell(record[name]))
      .map(
        ([name, property]) =>
          `${property.title ?? name}: ${formatAskUserValue(property, record[name])}`
      )
      .join(', ');
  }
  if (kind === 'rows') {
    const rows = Array.isArray(value) ? value : [];
    if (rows.length === 0) return 'None';
    return rows
      .map((row) =>
        rowProperties(schema)
          .filter(([name]) => isRecord(row) && !isBlank(row[name]))
          .map(([name, property]) => {
            const cell = (row as Record<string, unknown>)[name];
            return `${property.title ?? name}: ${formatAskUserValue(property, cell)}`;
          })
          .join(', ')
      )
      .join('; ');
  }
  return formatScalar(value);
}

/** An answer as the lines of its thread bubble (Figma 44242:80395): one line
 * for most answers; for package rows, one line per field, with fields that
 * share a layout row and unit joined ("Dimensions: 120 × 80 × 100 cm"). */
export function formatAskUserAnswerLines(
  schema: AskUserFieldSchema,
  value: unknown
): string[] {
  if (askUserFieldKind(schema) === 'form') {
    const record = isRecord(value) ? value : {};
    return formLayout(schema).flatMap((line) =>
      answerLine(line, withoutEmptyCells(record))
    );
  }
  if (askUserFieldKind(schema) !== 'rows') {
    return [withUnit(schema, formatAskUserValue(schema, value), value)];
  }
  const rows = (Array.isArray(value) ? value : []).filter(isRecord);
  if (rows.length === 0) return ['None'];
  const itemLabel = schema['x-ui']?.itemLabel;
  return rows.flatMap((row, index) => {
    const lines = rowLayout(schema).flatMap((line) =>
      answerLine(line, row as Record<string, unknown>)
    );
    return rows.length > 1 && itemLabel
      ? [`${capitalize(itemLabel)} ${index + 1}`, ...lines]
      : lines;
  });
}

function answerLine(
  line: [string, AskUserFieldSchema][],
  row: Record<string, unknown>
): string[] {
  const unit = sharedUnit(line);
  const group = line[0][1]['x-ui']?.row;
  const allGiven = line.every(([name]) => !isBlank(row[name]));
  if (unit && group && allGiven) {
    const joined = line
      .map(([name, property]) => formatAskUserValue(property, row[name]))
      .join(' × ');
    return [`${capitalize(group)}: ${joined} ${unit}`];
  }
  return line
    .filter(([name]) => !isBlank(row[name]))
    .map(
      ([name, property]) =>
        `${property.title ?? name}: ${withUnit(
          property,
          formatAskUserValue(property, row[name]),
          row[name]
        )}`
    );
}

function withUnit(
  schema: AskUserFieldSchema,
  text: string,
  value: unknown
): string {
  const unit = schema['x-ui']?.unit;
  return unit && typeof value === 'number' ? `${text} ${unit}` : text;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatScalar(value: unknown): string {
  if (isBlank(value)) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function questionLabel(question: AskUserQuestion): string {
  return question.header || question.question;
}

/** The user bubble shown for a submitted panel: one "Label: answer" line
 * per question. */
export function summarizeAskUserAnswers(
  request: AskUserRequest,
  content: Record<string, unknown>
): string {
  return request.questions
    .map(
      (question) =>
        `${questionLabel(question)}: ${formatAskUserValue(
          question.schema,
          content[question.key]
        )}`
    )
    .join('\n');
}

/** The accepted answers a later user turn gave to `requestId`, if any. */
export function findAskUserAnswers(
  messages: AIChatMessage[],
  fromIndex: number,
  requestId: string
): AskUserAnswers | undefined {
  for (let index = fromIndex + 1; index < messages.length; index += 1) {
    const answers = messages[index].answers;
    if (answers?.id === requestId) return answers;
  }
  return undefined;
}

function normalizeAskUserQuestion(raw: unknown): AskUserQuestion | null {
  if (!isRecord(raw)) return null;
  if (typeof raw.key !== 'string' || !raw.key) return null;
  const question = typeof raw.question === 'string' ? raw.question : '';
  const header = typeof raw.header === 'string' ? raw.header : '';
  if (!question && !header) return null;
  return {
    key: raw.key,
    header,
    question,
    schema: isRecord(raw.schema) ? (raw.schema as AskUserFieldSchema) : {},
  };
}

/** Validates an `ask_user` frame (live or persisted) -- null when it has no
 * id or no usable question, since there'd be nothing to ask or answer. */
export function normalizeAskUser(raw: unknown): AskUserRequest | null {
  if (!isRecord(raw) || typeof raw.id !== 'string' || !raw.id) return null;
  const questions = Array.isArray(raw.questions)
    ? raw.questions
        .map(normalizeAskUserQuestion)
        .filter((question): question is AskUserQuestion => question !== null)
    : [];
  if (questions.length === 0) return null;
  return {
    id: raw.id,
    message: typeof raw.message === 'string' ? raw.message : '',
    questions,
  };
}

export function normalizeAskUserAnswers(raw: unknown): AskUserAnswers | null {
  if (!isRecord(raw) || typeof raw.id !== 'string' || !raw.id) return null;
  if (raw.action !== 'accept' && raw.action !== 'cancel') return null;
  return {
    id: raw.id,
    action: raw.action,
    content: isRecord(raw.content) ? raw.content : undefined,
  };
}

export function normalizeActionResult(
  raw: unknown
): ChatActionResultPayload | null {
  if (!isRecord(raw) || typeof raw.action !== 'string' || !raw.action) {
    return null;
  }
  return {
    action: raw.action,
    data: raw.data,
    assumed: isRecord(raw.assumed)
      ? (raw.assumed as ChatActionResultPayload['assumed'])
      : undefined,
  };
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function numberOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Which search a lane search result belongs to, without reading its quotes. */
export function laneSearchId(raw: unknown): string | null {
  return isRecord(raw) ? stringOrNull(raw.search_id) : null;
}

/** True for a path inside this app, so a link can never leave the site. It
 * must be a path, not a full URL (the router would read that as relative),
 * and the URL parser must keep it on this origin (it reads `//host`, `/\host`
 * and tab-split forms as another one). */
export function isInAppPath(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  try {
    return (
      new URL(path, window.location.origin).origin === window.location.origin
    );
  } catch {
    return false;
  }
}

export function normalizeLaneSearchData(raw: unknown): LaneSearchResultData {
  const data = isRecord(raw) ? raw : {};
  const quotes = (Array.isArray(data.quotes) ? data.quotes : []).filter(
    isRecord
  );
  return {
    searchId: laneSearchId(data),
    done: data.done === true,
    quotes: quotes.map((quote): LaneSearchQuote => ({
      quoteId: stringOrNull(quote.quote_id),
      ref: stringOrNull(quote.ref),
      vendor: stringOrNull(quote.vendor),
      mode: stringOrNull(quote.mode),
      carrier: stringOrNull(quote.carrier),
      carrierLogoUrl: stringOrNull(quote.carrier_logo_url),
      departsAt: stringOrNull(quote.departs_at),
      route: stringOrNull(quote.route),
      transitMinutes: numberOrNull(quote.transit_minutes),
      serviceLevel: stringOrNull(quote.service_level),
      // Prices arrive as exact decimal strings ("4820.00").
      price: numberOrNull(quote.price),
      currency: stringOrNull(quote.currency),
      validUntil: stringOrNull(quote.valid_until),
      trucking: normalizeTrucking(quote.door_to_door, quote.trucking_ends),
      legs: recordList(quote.legs).map(normalizeLeg),
      charges: recordList(quote.charges).map(normalizeCharge),
      pickup: normalizeTruckingLeg(
        isRecord(quote.trucking_legs) ? quote.trucking_legs.pickup : null
      ),
      delivery: normalizeTruckingLeg(
        isRecord(quote.trucking_legs) ? quote.trucking_legs.delivery : null
      ),
    })),
  };
}

function recordList(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function normalizeLeg(leg: Record<string, unknown>): LaneSearchLeg {
  return {
    mode: stringOrNull(leg.mode),
    carrier: stringOrNull(leg.carrier),
    logoUrl: stringOrNull(leg.logo_url),
    origin: stringOrNull(leg.origin),
    destination: stringOrNull(leg.destination),
    departsAt: stringOrNull(leg.departs_at),
    arrivesAt: stringOrNull(leg.arrives_at),
    serviceRef: stringOrNull(leg.service_ref),
  };
}

function normalizeCharge(charge: Record<string, unknown>): LaneSearchCharge {
  return {
    label: stringOrNull(charge.label),
    amount: numberOrNull(charge.amount),
  };
}

/** A quote's pickup or delivery trucking; null when it has none to show. */
function normalizeTruckingLeg(raw: unknown): LaneSearchTruckingLeg | null {
  if (!isRecord(raw)) return null;
  return {
    from: stringOrNull(raw.from),
    to: stringOrNull(raw.to),
    carrier: stringOrNull(raw.carrier),
    serviceLevel: stringOrNull(raw.service_level),
    price: numberOrNull(raw.price),
    currency: stringOrNull(raw.currency),
    transitMinutes: numberOrNull(raw.transit_minutes),
  };
}

/** "10/27/26" from an ISO date or date-time; the text itself when it is not one. */
export function formatShortDate(value: string | null): string | null {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${Number(month)}/${Number(day)}/${year.slice(-2)}`;
}

const TRUCKING_STATUSES: readonly string[] = [
  'pending',
  'incomplete',
  'complete',
];

/** A quote's trucking summary; null when the search didn't ask for trucking
 * (or the block isn't one this card knows how to show). `ends` is how each end
 * ended; a priced end is also known from the summary's own prices. */
function normalizeTrucking(
  raw: unknown,
  ends: unknown
): LaneSearchTrucking | null {
  if (!isRecord(raw)) return null;
  const status = raw.status;
  if (typeof status !== 'string' || !TRUCKING_STATUSES.includes(status)) {
    return null;
  }
  const states = isRecord(ends) ? ends : {};
  return {
    total: numberOrNull(raw.total),
    currency: stringOrNull(raw.currency),
    status: status as LaneSearchTrucking['status'],
    pickup: normalizeTruckingEnd(states.pickup, raw.pickup),
    delivery: normalizeTruckingEnd(states.delivery, raw.delivery),
  };
}

function normalizeTruckingEnd(
  state: unknown,
  priced: unknown
): LaneSearchTruckingEnd | null {
  const record = isRecord(state) ? state : null;
  const price = isRecord(priced) ? priced : null;
  if (!record && !price) return null;
  return {
    status: stringOrNull(record?.status) ?? (price ? 'priced' : null),
    reason: stringOrNull(record?.reason),
    price: numberOrNull(price?.price),
    currency: stringOrNull(price?.currency),
  };
}

/** "45 m", "13 h", "13 h 30 m", "2 d 4 h" -- whole minutes, largest units
 * first (Figma writes durations as "13 h"). */
export function formatTransitMinutes(minutes: number | null): string | null {
  if (minutes === null || minutes < 0) return null;
  const total = Math.round(minutes);
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  if (hours > 0) return mins > 0 ? `${hours} h ${mins} m` : `${hours} h`;
  return `${mins} m`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** A departure's local time and date at its origin, e.g. { time: "10:30 AM",
 * date: "Wed, 8/19/26" }, read straight from the ISO string so the origin's
 * own offset is kept (not converted to the viewer's zone). Null when the
 * value isn't an ISO date-time. */
export function formatLocalDeparture(
  value: string | null
): { time: string; date: string } | null {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match.map(Number);
  const weekday =
    WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const period = hour < 12 ? 'AM' : 'PM';
  return {
    time: `${hour12}:${String(minute).padStart(2, '0')} ${period}`,
    date: `${weekday}, ${month}/${day}/${String(year).slice(-2)}`,
  };
}

/** A routing string's airports, in order -- "LAX-NRT-SIN", "LAX → NRT" and
 * "LAX/NRT" all split the same way. */
export function routingAirports(routing: string | null): string[] {
  if (!routing) return [];
  return routing
    .split(/\s*(?:->|→|>|–|—|-|\/|,)\s*/)
    .map((airport) => airport.trim())
    .filter(Boolean);
}

/** "Direct" for a two-airport routing, else the intermediate stop count;
 * null when the routing doesn't name at least an origin and destination. */
export function routingStopsLabel(airports: string[]): string | null {
  if (airports.length < 2) return null;
  const stops = airports.length - 2;
  if (stops === 0) return 'Non stop';
  return stops === 1 ? '1 stop' : `${stops} stops`;
}

export function formatQuotePrice(
  amount: number | null,
  currency: string | null
): string | null {
  if (amount === null) return null;
  if (currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        // Whole amounts read as "$4,820" (as in Figma); keep cents otherwise.
        ...(Number.isInteger(amount) ? { maximumFractionDigits: 0 } : {}),
      }).format(amount);
    } catch {
      // Not an ISO 4217 code -- fall through to a plain amount + label.
    }
  }
  const formatted = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(amount);
  return currency ? `${formatted} ${currency}` : formatted;
}

/** One trucking end in words: its price, or what happened to it. */
function truckingEndLine(
  label: string,
  end: LaneSearchTruckingEnd,
  currency: string | null
): string {
  const name = `${label} trucking`;
  const price = formatQuotePrice(end.price, end.currency ?? currency);
  if (end.status === 'priced' && price) return `${name} ${price}`;
  if (end.status === 'pending')
    return `Pricing ${label.toLowerCase()} trucking…`;
  if (end.reason === 'outside_us')
    return `${name} isn't available outside the US`;
  if (end.reason === 'no_address' || end.reason === 'city_only') {
    return `${name} needs a street address`;
  }
  if (end.reason === 'bad_address') return `${name} address wasn't recognised`;
  if (end.status === 'unavailable') return `${name} isn't available`;
  return `${name} has no price`;
}

/** The lines under a quote that asked for trucking: the door-to-door total once
 * every end is priced, else one line for each end the search asked for, with its
 * price or what happened to it. Empty without trucking. */
export function truckingLines(trucking: LaneSearchTrucking | null): string[] {
  if (!trucking) return [];
  const total = formatQuotePrice(trucking.total, trucking.currency);
  if (trucking.status === 'complete' && total) {
    return [`${total} door to door, incl. trucking`];
  }
  const lines = [
    trucking.pickup &&
      truckingEndLine('Pickup', trucking.pickup, trucking.currency),
    trucking.delivery &&
      truckingEndLine('Delivery', trucking.delivery, trucking.currency),
  ].filter((line): line is string => Boolean(line));
  if (lines.length > 0) return lines;
  // A result that carries no per-end states: only the overall state is known.
  return trucking.status === 'pending'
    ? ['Pricing pickup and delivery trucking…']
    : ['Trucking not fully priced'];
}
